import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, ArrowUpRight, Loader2, Check } from "lucide-react";
import { SERVICE_OPTIONS } from "../data/services";
import Reveal from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const location = useLocation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [services, setServices] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Pre-select a service if arriving from a services CTA (?service=...)
    const params = new URLSearchParams(location.search);
    const preset = params.get("service");
    if (preset && SERVICE_OPTIONS.includes(preset)) {
      setServices([preset]);
    }
  }, [location.search]);

  const update = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toggleService = (svc) => {
    setServices((prev) =>
      prev.includes(svc) ? prev.filter((s) => s !== svc) : [...prev, svc],
    );
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Email is required.";
    else if (!EMAIL_RE.test(form.email.trim()))
      next.email = "Enter a valid email address.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        services,
        message: form.message.trim() || null,
        website: form.website, // honeypot
      });
      setSent(true);
      toast.success("Message sent — I'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "", website: "" });
      setServices([]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-black/20 py-4 font-body text-lg text-[#111111] placeholder:text-neutral-400 focus:border-[#2f4bff] focus:outline-none transition-colors";

  return (
    <main
      data-testid="contact-page"
      className="pt-32 md:pt-40 min-h-screen bg-[#f4f3ee]"
    >
      <section className="relative z-10 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20">
            {/* Left — CTA + info */}
            <div>
              <Reveal>
                <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#2f4bff]">
                  / Contact us
                </span>
                <h1 className="mt-6 font-head text-6xl md:text-8xl font-extrabold tracking-tight leading-[0.9] text-[#111111]">
                  Let&apos;s
                  <br />
                  <span className="text-[#2f4bff]">build.</span>
                </h1>
                <p className="mt-8 font-body text-lg text-neutral-600 max-w-md leading-relaxed">
                  Tell me about your business and what you need — a quick fix, a
                  new feature, or a full upgrade. I read and reply to every
                  message personally.
                </p>
              </Reveal>

              <Reveal delay={0.1}>
                <div className="mt-12 space-y-6">
                  <a
                    href="mailto:shawnrye@hotmail.com"
                    data-testid="contact-info-email"
                    className="group flex items-center gap-4"
                  >
                    <span className="h-11 w-11 grid place-items-center border border-black/15 text-[#2f4bff] group-hover:border-[#2f4bff] group-hover:bg-[#2f4bff] group-hover:text-white transition-colors">
                      <Mail className="h-5 w-5" />
                    </span>
                    <span className="font-mono-tech text-base md:text-lg text-[#111111]">
                      shawnrye@hotmail.com
                    </span>
                  </a>
                  <a
                    href="tel:4017440703"
                    data-testid="contact-info-phone"
                    className="group flex items-center gap-4"
                  >
                    <span className="h-11 w-11 grid place-items-center border border-black/15 text-[#2f4bff] group-hover:border-[#2f4bff] group-hover:bg-[#2f4bff] group-hover:text-white transition-colors">
                      <Phone className="h-5 w-5" />
                    </span>
                    <span className="font-mono-tech text-base md:text-lg text-[#111111]">
                      401-744-0703
                    </span>
                  </a>
                </div>
              </Reveal>
            </div>

            {/* Right — form */}
            <Reveal delay={0.15}>
              <div className="bg-white border border-black/10 p-8 md:p-10 shadow-[0_30px_60px_-40px_rgba(17,17,17,0.35)]">
                {sent ? (
                  <div
                    data-testid="contact-success"
                    className="min-h-[420px] flex flex-col items-start justify-center"
                  >
                    <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#2f4bff]">
                      / Message received
                    </span>
                    <h2 className="mt-4 font-head text-4xl font-bold tracking-tight text-[#111111]">
                      Thank you.
                    </h2>
                    <p className="mt-4 font-body text-neutral-600 max-w-sm">
                      Your message is on its way. I&apos;ll get back to you at
                      the email you provided.
                    </p>
                    <button
                      data-testid="contact-send-another"
                      onClick={() => setSent(false)}
                      className="mt-8 inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.15em] text-[#111111] border border-black/20 px-6 py-3 hover:border-[#2f4bff] hover:text-[#2f4bff] transition-colors"
                    >
                      Send another
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-testid="contact-form"
                    noValidate
                    className="space-y-8"
                  >
                    {/* Honeypot — hidden from real users; bots fill it. */}
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: "-9999px",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                      }}
                    >
                      <label>
                        Website (leave blank)
                        <input
                          type="text"
                          name="website"
                          tabIndex={-1}
                          autoComplete="off"
                          value={form.website}
                          onChange={update("website")}
                        />
                      </label>
                    </div>
                    <div>
                      <label className="font-mono-tech text-xs uppercase tracking-[0.15em] text-neutral-500">
                        Name <span className="text-[#2f4bff]">*</span>
                      </label>
                      <input
                        data-testid="contact-input-name"
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Your name"
                        className={inputCls}
                      />
                      {errors.name && (
                        <p
                          data-testid="contact-error-name"
                          className="mt-2 font-mono-tech text-xs text-red-500"
                        >
                          {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="font-mono-tech text-xs uppercase tracking-[0.15em] text-neutral-500">
                        Email <span className="text-[#2f4bff]">*</span>
                      </label>
                      <input
                        data-testid="contact-input-email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@yourbusiness.com"
                        className={inputCls}
                      />
                      {errors.email && (
                        <p
                          data-testid="contact-error-email"
                          className="mt-2 font-mono-tech text-xs text-red-500"
                        >
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="font-mono-tech text-xs uppercase tracking-[0.15em] text-neutral-500">
                        Phone
                      </label>
                      <input
                        data-testid="contact-input-phone"
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="Optional"
                        className={inputCls}
                      />
                    </div>

                    {/* Services multi-select */}
                    <div data-testid="contact-services">
                      <label className="font-mono-tech text-xs uppercase tracking-[0.15em] text-neutral-500">
                        Services you&apos;re interested in
                      </label>
                      <div className="mt-4 flex flex-wrap gap-2.5">
                        {SERVICE_OPTIONS.map((svc) => {
                          const active = services.includes(svc);
                          return (
                            <button
                              key={svc}
                              type="button"
                              data-testid={`service-chip-${svc
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")
                                .replace(/(^-|-$)/g, "")}`}
                              aria-pressed={active}
                              onClick={() => toggleService(svc)}
                              className={`inline-flex items-center gap-1.5 px-3.5 py-2 font-mono-tech text-[11px] uppercase tracking-[0.1em] border transition-colors duration-200 ${
                                active
                                  ? "bg-[#2f4bff] border-[#2f4bff] text-white"
                                  : "bg-transparent border-black/20 text-neutral-600 hover:border-[#2f4bff] hover:text-[#2f4bff]"
                              }`}
                            >
                              {active && <Check className="h-3 w-3" />}
                              {svc}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Project details */}
                    <div>
                      <label className="font-mono-tech text-xs uppercase tracking-[0.15em] text-neutral-500">
                        Project details
                      </label>
                      <textarea
                        data-testid="contact-input-message"
                        rows={4}
                        value={form.message}
                        onChange={update("message")}
                        placeholder="Tell me what you're building or what needs fixing..."
                        className={`${inputCls} resize-none`}
                      />
                    </div>

                    <button
                      data-testid="contact-submit-button"
                      type="submit"
                      disabled={submitting}
                      className="group w-full inline-flex items-center justify-center gap-3 bg-[#2f4bff] text-white font-mono-tech text-sm uppercase tracking-[0.12em] px-8 py-5 hover:bg-[#111111] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Sending
                        </>
                      ) : (
                        <>
                          Send message
                          <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
